import { useEffect, useState } from 'react';
import { ApiError, toUserMessage } from '@platform/api-client';

import { Alert } from '@/shared/ui';
import type { LoginRequest } from '@/shared/types/auth.type';
import { useResendVerificationByLogin } from '../api/use-auth.mutation';
import { EMAIL_VERIFY_RESEND_COOLDOWN_SECONDS } from '../constants/cooldown.constant';

const SECOND_MS = 1000;

/**
 * 인증 메일 재발송 — **가입된 주소로** 보낸다.
 *
 * 로그인 폼에 친 아이디·비밀번호로 계정을 특정하므로 이메일을 다시 묻지 않는다.
 * 가입 때 이메일을 잘못 쳤다면 사용자는 그 주소를 모르고, 물어봐도 올바른 주소를
 * 넣을 테니 메일은 영영 안 온다. 그 경우는 상위의 "가입 정보 확인"이 맡는다.
 *
 * `onSent` — 메일이 나갔거나(성공) 방금 나간 상태(쿨다운)일 때 부른다.
 * 상위는 이걸 받아 "그래도 안 오면 가입 정보 확인" 안내를 띄운다.
 */
export function ResendVerificationButton({
  account,
  onSent,
}: {
  account: LoginRequest;
  onSent?: () => void;
}) {
  const [seconds, setSeconds] = useState(0);
  const { mutate, isPending, isSuccess, error } = useResendVerificationByLogin();

  useEffect(() => {
    if (seconds <= 0) return;
    const t = window.setTimeout(() => setSeconds((s) => s - 1), SECOND_MS);
    return () => window.clearTimeout(t);
  }, [seconds]);

  const handleClick = () => {
    mutate(account, {
      onSuccess: () => {
        setSeconds(EMAIL_VERIFY_RESEND_COOLDOWN_SECONDS);
        onSent?.();
      },
      onError: (err) => {
        // 서버 쿨다운(가입 직후 포함)에 걸렸으면 클라 타이머도 맞춘다.
        // 메일은 직전에 이미 나갔으므로 상위에도 보냈다고 알린다.
        if (
          err instanceof ApiError &&
          err.code === 'AUTH_EMAIL_VERIFY_RESEND_COOLDOWN'
        ) {
          setSeconds(EMAIL_VERIFY_RESEND_COOLDOWN_SECONDS);
          onSent?.();
        }
      },
    });
  };

  const isCooldownError =
    error instanceof ApiError &&
    error.code === 'AUTH_EMAIL_VERIFY_RESEND_COOLDOWN';

  return (
    <div className="space-y-3">
      <button
          type="button"
          onClick={handleClick}
          disabled={isPending || seconds > 0}
          className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
      >
        {isPending
            ? '발송 중…'
            : seconds > 0
                ? `${seconds}초 후 재발송할 수 있습니다`
                : '인증 메일 재발송'}
      </button>
      {isSuccess && (
        <Alert tone="success">
          가입하신 이메일로 인증 메일을 재발송했습니다.
          <br />
          메일함(스팸함 포함)을 확인해 주세요.
        </Alert>
      )}
      {isCooldownError && (
        <Alert tone="info">
          인증 메일이 이미 발송되었습니다.
          <br />
          메일함(스팸함 포함)을 확인해 주세요.
        </Alert>
      )}
      {error && !isCooldownError && (
        <Alert tone="error">{toUserMessage(error)}</Alert>
      )}


    </div>
  );
}
